import React, { useCallback } from 'react';
import { Icon } from 'tea-component';
import { useIntl } from 'umi';

import {
  ColorSwatch,
  type IColorInfo,
} from '@/pages/datalab/components/ColorPalette';
import ColorPalettePopover from '@/pages/datalab/components/ColorPalettePopover';
import {
  NumberOperationSelect,
  NumberOperationValueInput,
  resolveValueOnOperationChange,
} from '@dbl/domainComponents/filter';
import type { TOperation } from '@dbl/core/filter';
import type { INumericalValueRule } from '../../type';
import NumericalIconSelect from './NumericalIconSelect';

export interface INumericalValuesRuleRowProps {
  rule: INumericalValueRule;
  onChange: (rule: INumericalValueRule) => void;
  onDelete: () => void;
}

const NumericalValuesRuleRow: React.FC<INumericalValuesRuleRowProps> = ({
  rule,
  onChange,
  onDelete,
}) => {
  const { formatMessage } = useIntl();
  const deleteLabel = formatMessage({ id: 'dbl_table_condition_delete' });

  const patchRule = useCallback(
    (patch: Partial<INumericalValueRule>) => {
      onChange({ ...rule, ...patch });
    },
    [onChange, rule],
  );

  const handleOperationChange = useCallback(
    (operation: TOperation) => {
      patchRule({
        operation,
        value: resolveValueOnOperationChange(
          operation,
          rule.value,
        ).map(String),
      });
    },
    [patchRule, rule.value],
  );

  const handleValueChange = useCallback(
    (columnValue: (string | number)[]) => {
      patchRule({ value: columnValue.map(String) });
    },
    [patchRule],
  );

  const handleColorChange = useCallback(
    (color: IColorInfo) => {
      patchRule({ color });
    },
    [patchRule],
  );

  const handleIconChange = useCallback(
    (icon: string) => {
      patchRule({ icon });
    },
    [patchRule],
  );

  return (
    <div className="dbl-numerical-values-grid__rule-row">
      <div className="dbl-numerical-values-grid__rule-inputs">
        <NumberOperationSelect
          className="dbl-numerical-values-grid__operation-select"
          operation={rule.operation}
          onChange={handleOperationChange}
        />
        <NumberOperationValueInput
          className="dbl-numerical-values-grid__value-input"
          operation={rule.operation}
          columnValue={rule.value}
          onChange={handleValueChange}
        />
      </div>

      <div className="dbl-numerical-values-grid__color-cell">
        <ColorPalettePopover
          value={rule.color}
          onChange={handleColorChange}
          popoverProps={{
            trigger: 'click',
            placement: 'bottom-end',
            positionFixed: false,
          }}
        >
          <div className="dbl-numerical-values-grid__color-swatch-container">
            <ColorSwatch
              className="dbl-numerical-values-grid__color-swatch"
              color={rule.color.hexa}
              size={23}
              borderRadius={4}
            />
            <Icon type="arrowdown" />
          </div>
        </ColorPalettePopover>
      </div>

      <NumericalIconSelect
        className="dbl-numerical-values-grid__icon-select"
        value={rule.icon}
        onChange={handleIconChange}
      />

      <span
        className="dbl-numerical-values-grid__action-delete"
        onClick={onDelete}
      >
        {deleteLabel}
      </span>
    </div>
  );
};

export default NumericalValuesRuleRow;

